import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { apprenantService, actionService, reportingService, contratService } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import ResponsiveTabs from '../components/ResponsiveTabs';
import { FileText, LogOut, AlertCircle, PenTool, Clock, MapPin, Calendar, BookOpen, Home, Briefcase, AlertTriangle, Send, GraduationCap, DoorOpen, Star, Upload, Loader2, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import SignaturePad from '../components/SignaturePad';
import JournalDemarches from '../components/JournalDemarches';
import BourseEmploi from '../components/BourseEmploi';
import MesCandidatures from '../components/MesCandidatures';
import DiagnosticCompetences from '../components/DiagnosticCompetences';
import SortieVolontaire from '../components/SortieVolontaire';
import EvaluationSatisfaction from '../components/EvaluationSatisfaction';
import NotificationsInApp from '../components/NotificationsInApp';
import Messagerie from '../components/Messagerie';
import ComptesRendusBeneficiaire from '../components/ComptesRendusBeneficiaire';
import { ClipboardList } from 'lucide-react';
import api from '../services/api';
import { formatDate } from '../utils/dateFormat';
import { Users } from 'lucide-react';

const FREINS_OPTIONS = ['Mobilité', 'Logement', 'Financier', 'Santé', 'Juridique', 'Addiction', 'Administratif', 'Autres'];

const FreinBadge = ({ frein }) => {
  const colors = {
    'Mobilité': 'bg-blue-100 text-blue-700',
    'Logement': 'bg-purple-100 text-purple-700',
    'Financier': 'bg-amber-100 text-amber-700',
    'Santé': 'bg-rose-100 text-rose-700',
    'Juridique': 'bg-red-100 text-red-700',
    'Addiction': 'bg-orange-100 text-orange-700',
    'Administratif': 'bg-teal-100 text-teal-700',
    'Autres': 'bg-slate-100 text-slate-700'
  };

  return (
    <span className={`px-3 py-1 rounded-full text-sm font-bold ${colors[frein] || 'bg-slate-100 text-slate-700'}`}>
      {frein}
    </span>
  );
};

const ApprenantDashboard = () => {
  const { user, logout } = useAuth();
  const [apprenant, setApprenant] = useState(null);
  const [actions, setActions] = useState([]);
  const [ateliers, setAteliers] = useState([]);
  const [stats, setStats] = useState(null);
  const [contrats, setContrats] = useState([]);
  const [signaturesRequises, setSignaturesRequises] = useState([]);
  const [justificatifsRequis, setJustificatifsRequis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({ freins: {}, freins_details: '' });
  const [showSignature, setShowSignature] = useState(false);
  const [selectedSignature, setSelectedSignature] = useState(null);
  const [uploadingJustificatif, setUploadingJustificatif] = useState(null);
  const signatureRef = useRef(null);
  
  // Déterminer l'onglet par défaut depuis l'URL
  const urlParams = new URLSearchParams(window.location.search);
  const defaultTab = urlParams.get('tab') === 'journal' ? 'journal' : 'accueil';
  const [activeTab, setActiveTab] = useState(defaultTab);

  useEffect(() => {
    loadData();
    // Nettoyer l'URL après avoir récupéré le paramètre
    if (urlParams.get('tab')) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const loadData = async () => {
    try {
      if (!user.apprenant_id) {
        toast.error('Votre compte n\'est pas lié à un profil bénéficiaire');
        setLoading(false);
        return;
      }

      const [bénéficiaireRes, actionsRes, statsRes, signaturesRes, contratsRes, ateliersRes] = await Promise.all([
        apprenantService.getById(user.apprenant_id),
        actionService.getAll(user.apprenant_id),
        actionService.getStats(user.apprenant_id),
        api.get('/signatures-requises'),
        api.get(`/contrats?apprenant_id=${user.apprenant_id}`).catch(() => ({ data: [] })),
        api.get('/ateliers').catch(() => ({ data: [] }))
      ]);
      
      setApprenant(bénéficiaireRes.data);
      setActions(actionsRes.data);
      setStats(statsRes.data);
      setSignaturesRequises(signaturesRes.data);
      setContrats(contratsRes.data || []);
      
      // Filtrer les ateliers où le bénéficiaire est inscrit
      // On vérifie p.id ET p.apprenant_id pour couvrir tous les cas (anciens + nouveaux docs)
      const allAteliers = ateliersRes.data || [];
      const mesAteliers = allAteliers.filter(atelier =>
        atelier.participants?.some(p => p.id === user.apprenant_id || p.apprenant_id === user.apprenant_id)
      ).map(atelier => ({
        ...atelier,
        participation: atelier.participants?.find(
          p => p.id === user.apprenant_id || p.apprenant_id === user.apprenant_id
        )
      }));
      setAteliers(mesAteliers);

      // Récupérer les absences à justifier (ateliers + actions individuelles)
      // depuis le backend, qui applique la règle métier et le libellé canonique.
      try {
        const absRes = await api.get('/emargement/mes-absences-a-justifier');
        setJustificatifsRequis(absRes.data || []);
      } catch (e) {
        console.error('Erreur chargement absences à justifier', e);
        setJustificatifsRequis([]);
      }
      
      // Convertir les freins de l'objet (backend) vers tableau (frontend)
      const freinsFromBackend = bénéficiaireRes.data.freins;
      const freinsArray = typeof freinsFromBackend === 'object' && freinsFromBackend !== null
        ? Object.keys(freinsFromBackend)
        : (Array.isArray(freinsFromBackend) ? freinsFromBackend : []);
      
      setFormData({
        freins: freinsArray,
        freins_details: bénéficiaireRes.data.freins_details || ''
      });
    } catch (error) {
      toast.error('Erreur de chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const toggleFrein = (frein) => {
    setFormData((prev) => {
      const currentFreins = Array.isArray(prev.freins) ? prev.freins : [];
      const newFreins = currentFreins.includes(frein)
        ? currentFreins.filter(f => f !== frein)
        : [...currentFreins, frein];
      return { ...prev, freins: newFreins };
    });
  };

  const handleSaveFreins = async () => {
    try {
      await apprenantService.update(user.apprenant_id, {
        ...apprenant,
        freins: formData.freins,
        freins_details: formData.freins_details
      });
      toast.success('Freins mis à jour avec succès');
      setEditMode(false);
      loadData();
    } catch (error) {
      toast.error('Erreur de mise à jour');
    }
  };

  const handleOpenSignature = (signature) => {
    setSelectedSignature(signature);
    setShowSignature(true);
  };

  const handleUploadJustificatif = async (eventId, participantId, file) => {
    if (!file) return;

    setUploadingJustificatif(eventId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('event_id', eventId);
      formData.append('participant_id', participantId);

      await api.post('/emargement/justificatif', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Justificatif téléversé avec succès');
      loadData();
    } catch (error) {
      toast.error('Erreur lors du téléversement');
    } finally {
      setUploadingJustificatif(null);
    }
  };

  const handleSign = async () => {
    if (!selectedSignature) return;
    
    try {
      const signatureData = signatureRef.current?.save() || 'signed';
      
      if (selectedSignature.type === 'action_beneficiaire') {
        await api.put(`/actions/${selectedSignature.id}/signature-apprenant`, { signature: signatureData });
      } else if (selectedSignature.type === 'atelier_beneficiaire') {
        await api.put(`/ateliers/${selectedSignature.id}/participant/${selectedSignature.participant_id}/signature`, { signature: signatureData });
      }
      
      toast.success('Signature enregistrée');
      setShowSignature(false);
      setSelectedSignature(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la signature');
    }
  };

  const handleGeneratePDF = async () => {
    const now = new Date();
    const mois = now.getMonth() + 1;
    const annee = now.getFullYear();

    try {
      const response = await reportingService.getSyntheseIndividuelle(
        user.apprenant_id,
        mois,
        annee
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ma_synthese_${mois}_${annee}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF généré avec succès');
    } catch (error) {
      toast.error('Erreur de génération du PDF');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-lg text-slate-600">Chargement...</div>
      </div>
    );
  }

  if (!apprenant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <p className="text-center text-slate-600">
              Votre compte n'est pas encore lié à un profil bénéficiaire.
              Contactez votre référent.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // RÈGLE MÉTIER : on lit les valeurs renvoyées par l'API qui applique déjà la
  // règle « seules les présences signées comptent ». Ne pas recalculer côté front.
  const quotaHeuresApp = stats?.quota_heures || 60;
  const actionsHoursFront = stats?.heures_actions || 0;
  const ateliersHoursFront = stats?.heures_ateliers || 0;
  const totalHoursFront = stats?.total_hours || 0;
  const heuresRestantesApp = stats?.heures_restantes ?? Math.max(quotaHeuresApp - totalHoursFront, 0);

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      <header className="backdrop-blur-xl bg-white/80 border-b border-slate-200 sticky top-0 z-50 py-3 sm:py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 flex flex-wrap justify-between items-center gap-3">
          <div className="min-w-0 flex-1">
            <h1 className="text-lg sm:text-3xl font-bold text-slate-900 truncate" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Mon Espace Bénéficiaire
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 mt-0.5 sm:mt-1 truncate">
              Bienvenue, {apprenant.prenom} {apprenant.nom}
            </p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <NotificationsInApp />
            <Button
              onClick={logout}
              data-testid="logout-button"
              variant="outline"
              className="rounded-xl border-slate-200 h-9 px-2 sm:px-3"
            >
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Déconnexion</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-4 sm:py-8">
        {/* Signatures requises - Toujours visible en haut */}
        {signaturesRequises.length > 0 && (
          <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300 rounded-3xl p-6 mb-6 shadow-lg">
            <CardContent className="p-0">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-amber-100 rounded-2xl">
                  <PenTool className="w-7 h-7 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-amber-900">
                    Signatures en attente
                  </h3>
                  <p className="text-sm text-amber-700">
                    Vous avez {signaturesRequises.length} événement(s) nécessitant votre signature
                  </p>
                </div>
              </div>
              <div className="space-y-3">
                {signaturesRequises.map((sig, index) => (
                  <div key={index} className="bg-white rounded-2xl border border-amber-200 p-4 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-amber-100 text-amber-700">
                            {sig.type === 'atelier_beneficiaire' ? 'Atelier' : 'Action'}
                          </Badge>
                          {sig.type_atelier && (
                            <Badge variant="outline" className="text-slate-600">
                              {sig.type_atelier.replace(/_/g, ' ')}
                            </Badge>
                          )}
                        </div>
                        <p className="font-bold text-lg text-slate-900">{sig.titre}</p>
                        <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-slate-600">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4 text-amber-600" />
                            {formatDate(sig.date)}
                          </span>
                          {sig.heure_debut && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4 text-amber-600" />
                              {sig.heure_debut} - {sig.heure_fin}
                            </span>
                          )}
                          {sig.zone_geographique && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4 text-amber-600" />
                              {sig.zone_geographique}
                            </span>
                          )}
                        </div>
                      </div>
                      <Button
                        onClick={() => handleOpenSignature(sig)}
                        className="bg-amber-600 hover:bg-amber-700 rounded-xl px-6 shadow-md"
                      >
                        <PenTool className="w-4 h-4 mr-2" />
                        Signer ma présence
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Justificatifs requis */}
        {justificatifsRequis.length > 0 && (
          <Card className="bg-gradient-to-r from-orange-50 to-red-50 border-orange-300 rounded-3xl p-6 mb-6 shadow-lg">
            <CardContent className="p-0">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-orange-100 rounded-2xl">
                  <AlertTriangle className="w-7 h-7 text-orange-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-orange-900">
                    Justificatifs d'absence requis
                  </h3>
                  <p className="text-sm text-orange-700">
                    Vous avez {justificatifsRequis.length} absence(s) justifiée(s) nécessitant un document
                  </p>
                </div>
              </div>
              <div className="space-y-3">
                {justificatifsRequis.map((j, index) => {
                  // Compatibilité : event_id (nouveau format) ou atelier_id (ancien format frontend)
                  const eventId = j.event_id || j.atelier_id;
                  const typeLabel = j.type === 'action' ? 'RDV individuel' : 'Atelier';
                  return (
                    <div key={index} className="bg-white rounded-2xl border border-orange-200 p-4 shadow-sm">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex flex-wrap gap-2 mb-2">
                            <Badge className="bg-orange-100 text-orange-700">
                              Absence justifiée
                            </Badge>
                            <Badge variant="outline" className="border-slate-300 text-slate-600">
                              {typeLabel}
                            </Badge>
                          </div>
                          <p className="font-bold text-lg text-slate-900">{j.titre}</p>
                          <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4 text-orange-600" />
                              {formatDate(j.date)}
                            </span>
                            {j.heure_debut && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4 text-orange-600" />
                                {j.heure_debut}
                              </span>
                            )}
                          </div>
                        </div>
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            className="hidden"
                            onChange={(e) => handleUploadJustificatif(eventId, j.participant_id, e.target.files?.[0])}
                            disabled={uploadingJustificatif === eventId}
                          />
                          <Button
                            className="bg-orange-600 hover:bg-orange-700 rounded-xl px-6 shadow-md"
                            asChild
                          >
                            <span>
                              {uploadingJustificatif === eventId ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Upload className="w-4 h-4 mr-2" />
                              )}
                              {uploadingJustificatif === eventId ? 'Envoi...' : 'Téléverser justificatif'}
                            </span>
                          </Button>
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Onglets de navigation (responsive) */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <ResponsiveTabs
            asTabsListReplacement
            value={activeTab}
            onValueChange={setActiveTab}
            listClassName="bg-white rounded-2xl p-1 shadow-sm"
            tabClassName="rounded-xl px-3 lg:px-4 data-[state=active]:bg-slate-100"
            tabs={[
              { value: 'accueil', label: 'Mon profil', icon: Home },
              { value: 'suivi', label: 'Mon suivi', icon: ClipboardList },
              { value: 'competences', label: 'Compétences', icon: GraduationCap },
              { value: 'bourse', label: "Bourse à l'Emploi", icon: Briefcase },
              { value: 'candidatures', label: 'Mes Candidatures', icon: Send },
              { value: 'messagerie', label: 'Messagerie', icon: MessageCircle },
              { value: 'journal', label: 'Journal de Démarches', icon: BookOpen },
              { value: 'sortie', label: 'Sortie', icon: DoorOpen },
              { value: 'satisfaction', label: 'Satisfaction', icon: Star },
            ]}
          />

          {/* Onglet Mon suivi (comptes-rendus de RDV, lecture seule) */}
          <TabsContent value="suivi">
            {user?.apprenant_id && (
              <ComptesRendusBeneficiaire
                beneficiaireId={user.apprenant_id}
                beneficiaireNom={`${user.prenom || ''} ${user.nom || ''}`}
                readOnly={true}
              />
            )}
          </TabsContent>

          {/* Onglet Compétences */}
          <TabsContent value="competences">
            <DiagnosticCompetences />
          </TabsContent>

          {/* Onglet Bourse à l'Emploi */}
          <TabsContent value="bourse">
            <BourseEmploi isBeneficiaire={true} canCreate={false} canEdit={false} canDelete={false} />
          </TabsContent>

          {/* Onglet Mes Candidatures */}
          <TabsContent value="candidatures">
            <MesCandidatures />
          </TabsContent>

          {/* Onglet Messagerie (bénéficiaire ↔ animateurs) */}
          <TabsContent value="messagerie">
            <Messagerie />
          </TabsContent>

          {/* Onglet Accueil / Profil */}
          <TabsContent value="accueil">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Section Contrat avec fin de contrat visible */}
            {contrats.length > 0 && (
              <Card className={`border-2 rounded-3xl p-6 shadow-sm ${
                contrats.some(c => c.date_fin) 
                  ? 'bg-red-50 border-red-200' 
                  : 'bg-emerald-50 border-emerald-200'
              }`}>
                <CardHeader className="pb-4">
                  <CardTitle className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
                    <Briefcase className="w-5 h-5" />
                    Mon Contrat de Travail
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {contrats.map((contrat, idx) => (
                    <div key={idx} className="bg-white rounded-xl p-4 border">
                      <div className="flex items-start justify-between">
                        <div>
                          <Badge className={`mb-2 ${contrat.actif ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}`}>
                            {contrat.type_contrat}
                          </Badge>
                          <p className="font-semibold text-slate-900">Signé le {new Date(contrat.date_signature).toLocaleDateString('fr-FR')}</p>
                          {contrat.duree_mois && (
                            <p className="text-sm text-slate-600">Durée : {contrat.duree_mois} mois</p>
                          )}
                        </div>
                        {contrat.date_fin && (
                          <div className="bg-red-100 border border-red-300 rounded-xl p-3 text-center">
                            <AlertTriangle className="w-5 h-5 text-red-600 mx-auto mb-1" />
                            <p className="text-sm font-bold text-red-800">Fin de contrat</p>
                            <p className="text-lg font-bold text-red-600">
                              {new Date(contrat.date_fin).toLocaleDateString('fr-FR')}
                            </p>
                            {contrat.motif_fin && (
                              <Badge className="bg-red-200 text-red-800 mt-1 text-xs">
                                {contrat.motif_fin.replace(/_/g, ' ')}
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            <Card className="bg-white border-slate-200 rounded-3xl p-6 shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl font-bold" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  Mes Informations
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">Zone</div>
                    <div className="text-slate-900">{apprenant.zone_geographique}</div>
                  </div>
                  <div>
                    <div className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">N° Sécurité Sociale</div>
                    <div className="text-slate-900">{apprenant.numero_securite_sociale}</div>
                  </div>
                </div>
                <div>
                  <div className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">Adresse</div>
                  <div className="text-slate-900">{apprenant.adresse}</div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-200 rounded-3xl p-6 shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl font-bold" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  Mes Actions & Rendez-vous
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats && (
                  <div className="mb-4 p-4 bg-blue-50 rounded-xl">
                    <div className="flex items-baseline justify-between flex-wrap gap-2">
                      <div className="text-lg font-bold text-blue-900">
                        Total: {totalHoursFront}h / {quotaHeuresApp}h
                      </div>
                      <div className="text-sm font-semibold text-blue-700">
                        Reste : {heuresRestantesApp}h
                      </div>
                    </div>
                    <div className="text-sm text-blue-700 space-y-1 mt-2">
                      <div className="flex justify-between">
                        <span>{stats.action_count || 0} actions/RDV signés</span>
                        <span className="font-medium">{actionsHoursFront}h</span>
                      </div>
                      <div className="flex justify-between">
                        <span>{stats.atelier_count || 0} ateliers signés</span>
                        <span className="font-medium">{ateliersHoursFront}h</span>
                      </div>
                      {stats.activite_intervenant_count > 0 && (
                        <div className="flex justify-between">
                          <span>{stats.activite_intervenant_count} activités intervenant</span>
                          <span className="font-medium">{stats.heures_activites_intervenant || 0}h</span>
                        </div>
                      )}
                    </div>
                    {/* Barre de progression */}
                    <div className="mt-3">
                      <div className="w-full bg-blue-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${Math.min((totalHoursFront / quotaHeuresApp) * 100, 100)}%` }}
                        ></div>
                      </div>
                      <div className="text-xs text-blue-600 mt-1 text-right">
                        {Math.round((totalHoursFront / quotaHeuresApp) * 100)}% de l'objectif
                      </div>
                    </div>
                    <p className="text-xs text-blue-600 mt-2 italic">
                      Seules les présences signées sont décomptées du quota.
                    </p>
                  </div>
                )}
                
                {/* Section Ateliers inscrits */}
                {ateliers.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Ateliers inscrits
                    </h4>
                    <div className="space-y-2">
                      {ateliers.map((atelier) => (
                        <div
                          key={`atelier-${atelier.id}`}
                          className="p-4 bg-purple-50 rounded-xl border border-purple-200"
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="font-bold text-purple-900 flex items-center gap-2">
                                <Users className="w-4 h-4" />
                                {atelier.titre}
                              </div>
                              <div className="text-sm text-purple-700 mt-1">
                                {formatDate(atelier.date)} • {atelier.heure_debut} - {atelier.heure_fin}
                              </div>
                              {atelier.lieu && (
                                <div className="text-sm text-purple-600 mt-1 flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {atelier.lieu}
                                </div>
                              )}
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <div className="px-3 py-1 rounded-full text-sm font-bold bg-purple-100 text-purple-700">
                                Atelier
                              </div>
                              {atelier.participation?.statut_presence && (
                                <div className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                                  atelier.participation.statut_presence === 'present' 
                                    ? 'bg-emerald-100 text-emerald-700' 
                                    : atelier.participation.statut_presence === 'absence_justifiee'
                                      ? 'bg-amber-100 text-amber-700'
                                      : 'bg-red-100 text-red-700'
                                }`}>
                                  {atelier.participation.statut_presence === 'present' ? 'Présent' 
                                    : atelier.participation.statut_presence === 'absence_justifiee' ? 'Absence justifiée'
                                    : 'Absent'}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Section Actions/RDV */}
                {actions.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Rendez-vous individuels
                    </h4>
                    <div className="space-y-2">
                      {actions.map((action) => (
                        <div
                          key={action.id}
                          className="p-4 bg-slate-50 rounded-xl border border-slate-200"
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="font-bold text-slate-900">{action.titre}</div>
                              <div className="text-sm text-slate-600 mt-1">
                                {formatDate(action.date)} • {action.heure_debut} - {action.heure_fin}
                              </div>
                            </div>
                            <div className="px-3 py-1 rounded-full text-sm font-bold bg-amber-100 text-amber-700">
                              {action.duree_minutes} min
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {actions.length === 0 && ateliers.length === 0 && (
                  <p className="text-slate-500 text-center py-4">Aucune action ou atelier enregistré</p>
                )}
              </CardContent>
            </Card>
          </div>

          <div>
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 rounded-3xl p-6 shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl font-bold" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  Ma Synthèse Mensuelle
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-700 mb-4">
                  Téléchargez votre synthèse mensuelle avec toutes vos actions et votre progression.
                </p>
                <Button
                  onClick={handleGeneratePDF}
                  data-testid="generate-pdf-button"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Télécharger PDF
                </Button>
              </CardContent>
            </Card>
          </div>
            </div>
          </TabsContent>

          {/* Onglet Journal de Démarches */}
          <TabsContent value="journal">
            <JournalDemarches apprenantNom={`${apprenant.prenom} ${apprenant.nom}`} />
          </TabsContent>

          {/* Onglet Sortie Volontaire */}
          <TabsContent value="sortie">
            <SortieVolontaire />
          </TabsContent>

          {/* Onglet Évaluation Satisfaction */}
          <TabsContent value="satisfaction">
            <EvaluationSatisfaction />
          </TabsContent>
        </Tabs>
      </main>

      {/* Dialog Signature */}
      <Dialog open={showSignature} onOpenChange={setShowSignature}>
        <DialogContent className="sm:max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Signer ma présence
            </DialogTitle>
            {selectedSignature && (
              <DialogDescription>
                {selectedSignature.titre} — {formatDate(selectedSignature.date)}
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">Dessinez votre signature ci-dessous :</p>
            <SignaturePad ref={signatureRef} />
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setShowSignature(false); setSelectedSignature(null); }}
              className="rounded-xl"
            >
              Annuler
            </Button>
            <Button
              onClick={handleSign}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
            >
              Valider la signature
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApprenantDashboard;
